import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, DollarSign, Users, ShoppingBag, LogOut, Loader2 } from "lucide-react";
import { api, resolveMedia } from "@/lib/site";
import { useAuth } from "@/lib/auth";

const Tile = ({ icon: Icon, label, value }) => (
  <div className="card p-5" data-testid={`fan-stat-${label.toLowerCase()}`}>
    <Icon className="text-brand mb-3" size={20} strokeWidth={1.6} />
    <div className="font-display text-3xl text-white">{value}</div>
    <div className="text-white/45 text-xs uppercase tracking-widest mt-1">{label}</div>
  </div>
);

export default function FanDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/fan/dashboard").then(r=>setData(r.data)).catch(()=>{}); }, []);
  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-brand" /></div>;

  return (
    <div className="pt-28 pb-20 max-w-7xl mx-auto px-6" data-testid="fan-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-brand mb-2">Fan Dashboard</p>
          <h1 className="font-display text-4xl md:text-5xl text-white">Welcome back, {user?.name?.split(" ")[0] || "there"}</h1>
        </div>
        <button onClick={()=>{logout(); nav("/");}} data-testid="fan-logout" className="inline-flex items-center gap-2 border border-white/20 text-white/80 px-5 py-2.5 rounded-lg hover:border-brand hover:text-brand transition-colors">
          <LogOut size={15} /> Log out
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Tile icon={Heart} label="Following" value={data.stats.following} />
        <Tile icon={ShoppingBag} label="Purchases" value={data.stats.purchases} />
        <Tile icon={DollarSign} label="Total Support" value={`$${data.stats.total_spent}`} />
        <Tile icon={Users} label="Interests" value={data.stats.interests} />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-white">Following</h2>
            <Link to="/discover" className="text-brand text-sm hover:underline">Discover more →</Link>
          </div>
          <div className="space-y-3" data-testid="fan-following">
            {data.following.length === 0 && <p className="text-white/40 text-sm">You aren't following anyone yet. <Link to="/discover" className="text-brand">Start discovering →</Link></p>}
            {data.following.map(c => (
              <Link to={`/@${c.handle.replace('@','')}`} key={c.id} className="card p-3 flex items-center gap-4 hover:border-brand/50 transition-colors">
                <img src={resolveMedia(c.avatar || "/images/leather-hero.jpg")} alt={c.name} className="w-14 h-14 rounded-lg object-cover object-top" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold truncate">{c.name}</div>
                  <div className="text-white/50 text-xs">{c.handle}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-display text-2xl text-white mb-4">Recent Purchases</h2>
          <div className="space-y-3" data-testid="fan-purchases">
            {data.purchases.length === 0 && <p className="text-white/40 text-sm">No purchases yet.</p>}
            {data.purchases.map((p, i) => (
              <div key={i} className="card p-4 flex items-center justify-between">
                <div>
                  <div className="text-white text-sm">{p.creator_name}</div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">{p.kind}</div>
                </div>
                <div className="text-brand font-semibold">${p.amount}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
